import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    toggleChat,
    setActiveRoom,
    addMessage,
    fetchChatRooms,
    fetchMessages,
    updateReadStatus
} from '../../store/slices/chatSlice';
import socketService from '../../utils/socketService';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ChatOverlay = () => {
    const dispatch = useDispatch();
    const { isChatOpen, activeRoom, rooms } = useSelector((state) => state.chat);
    const { user, token } = useSelector((state) => state.auth);
    const [isConnected, setIsConnected] = useState(false);

    // Use ref to avoid stale closure in socket handlers
    const activeRoomRef = useRef(activeRoom);

    // Keep ref in sync with state
    useEffect(() => {
        activeRoomRef.current = activeRoom;
    }, [activeRoom]);

    // Initialize Socket
    useEffect(() => {
        if (token && user) {
            const socket = socketService.connect(token);
            setIsConnected(true);

            const handleReceiveMessage = (message) => {
                dispatch(addMessage(message));
            };

            const handleMessagesRead = ({ roomId }) => {
                // Use ref to get current activeRoom value
                if (activeRoomRef.current && activeRoomRef.current.id === roomId) {
                    dispatch(fetchMessages({ roomId }));
                }
                dispatch(updateReadStatus(roomId));
            };

            socket.on('receive_message', handleReceiveMessage);
            socket.on('messages_read', handleMessagesRead);

            return () => {
                socketService.off('receive_message', handleReceiveMessage);
                socketService.off('messages_read', handleMessagesRead);
                socketService.disconnect();
            };
        }
    }, [token, user, dispatch]);

    // Join rooms when list is fetched
    useEffect(() => {
        if (rooms.length > 0) {
            rooms.forEach(room => socketService.joinRoom(room.id));
        }
    }, [rooms]);

    // Fetch rooms on mount
    useEffect(() => {
        if (user && token) {
            dispatch(fetchChatRooms());
        }
    }, [dispatch, user, token]);

    if (!user) return null;

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
            {/* Chat Window */}
            {isChatOpen && (
                <div className="mb-4 w-80 md:w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-300 transform origin-bottom-right">

                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-4 text-white flex justify-between items-center shrink-0">
                        <div>
                            <h3 className="font-semibold text-lg">
                                {activeRoom
                                    ? (user.role === 'superadmin' ? activeRoom.participant?.name : 'Administrator')
                                    : 'Messages'}
                            </h3>
                            {activeRoom && <p className="text-xs text-blue-100 opacity-80">Online</p>}
                        </div>
                        <div className="flex items-center gap-2">
                            {activeRoom && (
                                <button
                                    onClick={() => dispatch(setActiveRoom(null))}
                                    className="p-1 hover:bg-white/20 rounded-full transition-colors text-xs"
                                >
                                    Back
                                </button>
                            )}
                            <button
                                onClick={() => dispatch(toggleChat())}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-hidden relative bg-gray-50">
                        {!activeRoom ? (
                            <ChatList />
                        ) : (
                            <ChatWindow />
                        )}
                    </div>

                    {/* Footer Disclaimer */}
                    <div className="bg-gray-100 p-2 text-center text-[10px] text-gray-400 border-t border-gray-200 shrink-0">
                        Messages older than 7 days are automatically removed.
                    </div>
                </div>
            )}

            {/* Floating Action Button */}
            <button
                onClick={() => dispatch(toggleChat())}
                className={`${isChatOpen ? 'bg-red-500 rotate-90' : 'bg-blue-600 hover:bg-blue-700'
                    } text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center relative`}
            >
                {isChatOpen ? (
                    <XMarkIcon className="w-6 h-6" />
                ) : (
                    <ChatBubbleLeftRightIcon className="w-6 h-6" />
                )}

                {!isChatOpen && rooms.reduce((acc, r) => acc + (r.unreadCount || 0), 0) > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                        {rooms.reduce((acc, r) => acc + (r.unreadCount || 0), 0)}
                    </span>
                )}
            </button>
        </div>
    );
};

export default ChatOverlay;
